// Shared environment options used in tech-stack tables / dropdowns
export const ENVIRONMENTS = ["All", "Proposed", "Future"];

/**
 * Single source of truth for cloud provider metadata.
 *  - key:     canonical lowercase identifier used in app logic / sessionStorage
 *  - label:   display value shown in dropdowns (SafSelect options)
 *  - aliases: substrings that may appear in upstream data feeds
 *             (e.g. tech-stack-mapping uses "Amazon", "Microsoft", "Google", "Oracle")
 */
const PROVIDER_DEFS = [
  { key: "aws", label: "AWS", aliases: ["aws", "amazon"] },
  { key: "azure", label: "Azure", aliases: ["azure", "microsoft"] },
  { key: "gcp", label: "GCP", aliases: ["gcp", "google"] },
  { key: "oci", label: "OCI", aliases: ["oci", "oracle"] },
];

// ---- Derived exports (kept for direct iteration in dropdowns) ----
export const CLOUD_PROVIDERS = PROVIDER_DEFS.map((p) => p.label);
export const MAJOR_CLOUD_PROVIDERS = PROVIDER_DEFS.map((p) => p.key);

// ccsm_datasource stores cloud_provider as uppercase ("AWS", "AZURE", ...)
export const CCSM_PROVIDER_MAP = PROVIDER_DEFS.reduce((acc, p) => {
  acc[p.key] = p.key.toUpperCase();
  return acc;
}, {});

// tech-stack-mapping alias lookup (kept for backward compatibility)
export const TECH_STACK_PROVIDER_ALIASES = PROVIDER_DEFS.reduce((acc, p) => {
  acc[p.key] = p.aliases;
  return acc;
}, {});

// ---- Helper functions (preferred API) ----

const toCleanLower = (v) =>
  v == null ? "" : v.toString().toLowerCase().trim();

/**
 * Convert any provider value ("AWS", "amazon", " Azure ") to its canonical
 * lowercase key ("aws", "azure", ...). Returns null for empty / "all" / unknown.
 */
export const normalizeCloudProvider = (value) => {
  const v = toCleanLower(value);
  if (!v || v === "all") return null;
  return PROVIDER_DEFS.find((p) => p.key === v)?.key ?? null;
};

/**
 * Uppercase value expected by ccsm_datasource ("AWS", "AZURE", "GCP", "OCI").
 * Returns null when no specific provider is selected.
 */
export const getCcsmProviderValue = (value) => {
  const key = normalizeCloudProvider(value);
  return key ? key.toUpperCase() : null;
};

/**
 * Does a tech-stack-mapping `provider` field (e.g. "Amazon", "Microsoft")
 * belong to the currently selected cloud provider? Returns true when no
 * provider filter is active.
 */
export const matchesTechStackProvider = (itemProvider, selectedProvider) => {
  const key = normalizeCloudProvider(selectedProvider);
  if (!key) return true;
  const def = PROVIDER_DEFS.find((p) => p.key === key);
  const item = toCleanLower(itemProvider);
  return def.aliases.some((alias) => item.includes(alias));
};

/**
 * True if the given upstream provider value maps to ANY of the major clouds
 * (AWS / Azure / GCP / OCI, including aliases). Used to keep third-party rows
 * visible while filtering out non-selected major clouds.
 */
export const isMajorCloudProviderValue = (itemProvider) => {
  const item = toCleanLower(itemProvider);
  if (!item) return false;
  return PROVIDER_DEFS.some((p) =>
    p.aliases.some((alias) => item.includes(alias))
  );
};
